import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaTimes } from "react-icons/fa";
import "../assets/css/EditTenant.css";
import "../assets/css/Projects.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { fetchProjectUsers } from "./taskService";
import { extractCreatedSubProjectId, uploadSubProjectFile } from "./projectFileService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";

const VISIBILITY_OPTIONS = ["Private", "Public", "Team"];

function AddSubproject({ onBack }) {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canAddSubprojectFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    subProjectName: "",
    shortCode: "",
    description: "",
    domain: "",
    category: "",
    startDate: "",
    endDate: "",
    actualStartDate: "",
    actualEndDate: "",
    visibility: "Team",
    statusId: "",
    userIds: []
  });
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/projects/${projectId}`);
        const p = response.data?.data || response.data;
        if (p) setProject(p);
        else setError("Project not found.");
      } catch (err) {
        console.error("Error fetching project:", err);
        setError(err.response?.data?.message || "Failed to load project.");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!projectId) {
        setLoadingUsers(false);
        return;
      }
      try {
        const projectUsers = await fetchProjectUsers(projectId, null);
        const mapped = projectUsers.map((u) => ({
          id: u.User_Id ?? u.user_id ?? u.id,
          name: (u.User_Name ?? u.user_name) || "—"
        })).filter((x) => x.id);
        setUsers(mapped);
      } catch (err) {
        console.error("Error fetching project users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get("/domains");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setDomains(arr.map((d) => ({ id: d.Domain_Id || d.domain_id || d.id, name: d.Domain_Name || d.domain_name || d.name || "—" })).filter((x) => x.id));
      } catch (err) {
        console.error("Error fetching domains:", err);
      } finally {
        setLoadingDomains(false);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/project-categories");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCategories(arr.map((c) => ({ id: c.Category_Id || c.category_id || c.id, name: c.Category_Name || c.category_name || c.name || "—" })).filter((x) => x.id));
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/project-statuses");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = arr
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "—"
          }))
          .filter((x) => x.id)
          .sort((a, b) => a.id - b.id);
        setStatuses(mapped);
        // Default Add Subproject status to "New" when available.
        setFormData((prev) => {
          if (prev.statusId) return prev;
          const newStatus = mapped.find((s) =>
            String(s.name || "").trim().toLowerCase() === "new"
          );
          const fallback = mapped[0]?.id;
          return {
            ...prev,
            statusId: String(newStatus?.id ?? fallback ?? "")
          };
        });
      } catch (err) {
        console.error("Error fetching project statuses:", err);
      } finally {
        setLoadingStatuses(false);
      }
    };
    loadUsers();
    fetchDomains();
    fetchCategories();
    fetchStatuses();
  }, [projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.userIds.includes(userId) ? prev.userIds.filter((id) => id !== userId) : [...prev.userIds, userId];
      return { ...prev, userIds: ids };
    });
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.userIds.length === users.length;

  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => {
      if (allUsersSelected) {
        return { ...prev, userIds: [] };
      }
      return { ...prev, userIds: users.map((u) => u.id) };
    });
  };

  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project || !formData.subProjectName || !formData.shortCode) {
      alert("Please fill in Subproject Name and Short Code.");
      return;
    }
    if (formData.startDate && formData.startDate < today) {
      alert("Start date cannot be in the past.");
      return;
    }
    if (formData.endDate && formData.endDate < today) {
      alert("End date cannot be in the past.");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    if (
      formData.actualStartDate &&
      formData.actualEndDate &&
      formData.actualEndDate < formData.actualStartDate
    ) {
      alert("Actual end date cannot be before actual start date.");
      return;
    }
    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.User_Id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      client_id: Number(project.client_id ?? project.client_id),
      Project_Id: Number(projectId),
      Sub_Project_Name: formData.subProjectName.trim(),
      Short_Code: formData.shortCode.trim(),
      Description: formData.description?.trim() || null,
      Domain_Id: formData.domain ? Number(formData.domain) : null,
      Category_Id: formData.category ? Number(formData.category) : null,
      Visibility: formData.visibility || "Team",
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Actual_Start_Date: formData.actualStartDate || null,
      Actual_End_Date: formData.actualEndDate || null,
      Updated_By: Number(updatedBy),
      User_Ids: formData.userIds.map((id) => Number(id)),
      Status_Id: formData.statusId ? parseInt(formData.statusId, 10) : 2
    };
    setSubmitting(true);
    try {
      const response = await axiosInstance.post("/sub-projects", payload);
      const data = response.data?.data ?? response.data;
      const created = data?.data ?? data?.sub_project ?? data;

      const newSpId =
        extractCreatedSubProjectId(created) ??
        (() => {
          const raw = created?.Sub_Project_Id ?? created?.sub_project_id ?? created?.id;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : null;
        })();

      if (pendingFiles.length > 0) {
        if (newSpId == null) {
          alert(
            "Subproject was created, but the response did not include a subproject id, so attachments were not uploaded. Add them from View or Edit subproject."
          );
          setPendingFiles([]);
        } else {
          for (const file of pendingFiles) {
            try {
              await uploadSubProjectFile(newSpId, file);
            } catch (uploadErr) {
              console.error("Subproject file upload after create:", uploadErr);
              alert(
                uploadErr.response?.data?.message ||
                  uploadErr.response?.data?.error ||
                  "Subproject created, but a file failed to upload. Add files from the subproject page."
              );
              break;
            }
          }
          setPendingFiles([]);
        }
      }

      alert("Subproject added successfully");
      if (onBack) onBack();
      else navigate(`/projects/${projectId}/view`);
    } catch (err) {
      console.error("Error creating subproject:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to create subproject.");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else navigate(`/projects/${projectId}/view`);
  };

  if (loading) {
    return (
      <div className="project-form-container edittenant-container">
        <div className="edittenant-loading">Loading project...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-form-container edittenant-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message" style={{ marginTop: 16 }}>{error || "Project not found."}</div>
      </div>
    );
  }

  return (
    <div className="project-form-container edittenant-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <p style={{ color: "#5E6C84", marginBottom: 24 }}>Project: {project.Project_Name || project.project_name}</p>
      <form className="edittenant-form" onSubmit={handleSubmit}>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flexWrap: "nowrap",
            marginBottom: 20
          }}
        >
          <div
            className="viewlead-card"
            style={{ flex: "2 1 0", minWidth: 260 }}
          >
            <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Subproject Name <span className="required">*</span></label>
              <input type="text" name="subProjectName" value={formData.subProjectName} onChange={handleInputChange} required placeholder="e.g., Junior Eats Mobile" />
            </div>
            <div className="form-group">
              <label>Short Code <span className="required">*</span></label>
              <input type="text" name="shortCode" value={formData.shortCode} onChange={handleInputChange} required placeholder="e.g., JREATSMo" maxLength={20} />
            </div>
            <div className="form-group">
              <label>Domain</label>
              <select name="domain" value={formData.domain} onChange={handleInputChange} disabled={loadingDomains}>
                <option value="">{loadingDomains ? "Loading domains..." : "Select Domain"}</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select name="category" value={formData.category} onChange={handleInputChange} disabled={loadingCategories}>
                <option value="">{loadingCategories ? "Loading categories..." : "Select Category"}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="statusId"
                value={formData.statusId}
                onChange={handleInputChange}
                disabled={loadingStatuses}
              >
                <option value="">{loadingStatuses ? "Loading statuses..." : "Select Status"}</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Visibility</label>
              <select name="visibility" value={formData.visibility} onChange={handleInputChange}>
                {VISIBILITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} min={today} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} min={formData.startDate || today} />
            </div>
            <div className="form-group">
              <label>Actual Start Date</label>
              <input type="date" name="actualStartDate" value={formData.actualStartDate} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Actual End Date</label>
              <input
                type="date"
                name="actualEndDate"
                value={formData.actualEndDate}
                onChange={handleInputChange}
                min={formData.actualStartDate || undefined}
              />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" placeholder="Subproject description..." />
          </div>
          {canAddSubprojectFiles ? (
            <div className="form-group issues-description-full" style={{ marginTop: 16 }}>
              <label>Attachments (optional)</label>
              <input
                type="file"
                className="issue-files-native-input"
                disabled={submitting}
                multiple
                onChange={(e) => {
                  const next = Array.from(e.target.files || []);
                  setPendingFiles((prev) => [...prev, ...next]);
                  e.target.value = "";
                }}
              />
              {pendingFiles.length > 0 ? (
                <ul className="issue-files-pending-list">
                  {pendingFiles.map((f, idx) => (
                    <li key={`${f.name}-${idx}`} className="issue-files-pending-item">
                      <span className="issue-files-pending-name">{f.name}</span>
                      <button
                        type="button"
                        className="issue-files-pending-remove"
                        onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                        disabled={submitting}
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          </div>
          </div>

          <div
            className="viewlead-card"
            style={{ flex: "1 1 0", minWidth: 260, maxWidth: 420 }}
          >
            <div className="form-section">
          <h3 className="viewlead-section-title" style={{ marginTop: 0 }}>Team Members</h3>
          <div className="form-group form-group-multi-select" style={{ marginTop: 8 }}>
            <div style={{ marginTop: 0, maxWidth: 320, width: "100%", alignSelf: "flex-start" }}>
              <div
                className="permissions-select-trigger"
                onClick={() => {
                  if (!loadingUsers) setShowTeamDropdown((prev) => !prev);
                }}
                style={{
                  opacity: loadingUsers ? 0.6 : 1,
                  cursor: loadingUsers ? "default" : "pointer"
                }}
              >
                <span className="permissions-select-text">
                  {loadingUsers
                    ? "Loading team members..."
                    : formData.userIds.length === 0
                      ? "Select team members"
                      : `${formData.userIds.length} member${formData.userIds.length > 1 ? "s" : ""} selected`}
                </span>
                <FaChevronDown
                  className={`permissions-select-chevron ${showTeamDropdown ? "open" : ""}`}
                />
              </div>
              {showTeamDropdown && !loadingUsers && (
                <div
                  className="permissions-select-panel"
                  style={{ maxHeight: 220, overflowY: "auto" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="permissions-select-item">
                    <input
                      type="checkbox"
                      checked={allUsersSelected}
                      onChange={handleToggleSelectAllUsers}
                    />
                    <span className="permissions-select-item-label">Select all</span>
                  </label>
                  {users.map((u) => (
                    <label
                      key={u.id}
                      className="permissions-select-item"
                    >
                      <input
                        type="checkbox"
                        checked={formData.userIds.includes(u.id)}
                        onChange={() => handleUserToggle(u.id)}
                      />
                      <span className="permissions-select-item-label">{u.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
          </div>
        </div>
        <div
          className="form-actions edittenant-actions"
          style={{ display: "inline-flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", gap: 10 }}
        >
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Adding..." : "Add Subproject"}
          </button>
          <button type="button" className="btn-cancel" onClick={goBack}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddSubproject;
